import * as React from 'react';
import { useState, useEffect } from 'react';

import { Button, Header, SpaceBetween, Spinner } from '@amzn/awsui-components-react-v3';
import { RequestContextComments } from 'src/components/datausageagreement/requestContextComments';
import {
  D3AValidation,
  D3AValidationList,
  D3AValidationResult,
  D3AValidationType,
  GetRequestRequest,
  GetRequestResult,
  UpdateRequestContextRequest,
  UpdateRequestContextResponse,
  UserRequestContext,
} from 'aws-sdk/clients/awsdatalakegladstonelambda';
import { UsageAgreementForm } from 'src/components/datausageagreement/usageAgreementForm';
import { ValidateFormDataWithD3a } from 'src/components/datausageagreement/validateFormDataWithD3a';
import { createRequestContext, getRequest, updateRequestContext } from 'src/api/permissions';
import { activeGroupOrWorkspaceId } from 'src/components/dataadvisory/constants';
import {
  DataAgreement,
  ValidateContextForDataAgreementOutput,
  Validation,
  ValidationResult,
} from 'aws-sdk/clients/d3aservice';
import { getDataAgreement, validateContextForDataAgreement } from 'src/api/d3a';
import { RequestContextMetadataContainer } from 'src/components/datausageagreement/requestContextMetadataContainer';
import { OmniDUAContext } from 'src/components/datausageagreement/widgets/OmniDUAContext';

export interface RequestContextDetailProps {
  setContentType: any;
  match: any;
  activeGroup: string;
  activeWorkspace?: any;
  userInfo?: any;
  requestContext?: UserRequestContext;
  setRequestContext: any;
  dataAgreementId?: string;
  workspaceNameMap?: Map<string, string>;
  displayMetadataContainer: boolean;
  displaySaveButton: boolean;
  omniContext?: OmniDUAContext;
}

export const RequestContextDetail = React.forwardRef((props: RequestContextDetailProps, ref: React.Ref<any>) => {
  const [loading, setLoading] = useState(true);

  // form state
  const [dataAgreement, setDataAgreement] = useState<DataAgreement>(undefined);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState([]);

  // validation state
  const [isValid, setIsValid] = useState<boolean>(false);
  const [isUnknown, setIsUnknown] = useState<boolean>(true);
  const [validationList, setValidationList] = useState<Validation[]>([]);
  const [validationErrorMessage, setValidationErrorMessage] = useState<string>(undefined);
  const [validationLoading, setValidationLoading] = useState<boolean>(false);
  const [overrideMessage, setOverrideMessage] = useState<string>('');

  const [saveLoading, setSaveLoading] = useState<boolean>(false);

  const [readOnly, setReadOnly] = useState<boolean>(true);

  if (!!ref) {
    React.useImperativeHandle(ref, () => ({
      save: save, // Expose the save function so the wizard can call it
    }));
  }

  useEffect(() => {
    props.setContentType('form');
    handleRefresh();
  }, []);

  useEffect(() => {
    if (props.requestContext?.dataString) {
      setFormData(JSON.parse(props.requestContext.dataString));
    }
    if (!!props.requestContext?.metadata?.d3AValidationContext) {
      setOverrideMessage(props.requestContext.metadata.d3AValidationContext.overrideMessage);
      setIsValid(props.requestContext.metadata.d3AValidationContext.isValid);
      setIsUnknown(false);
      setValidationList(
        convertD3aValidationsToValidations(props.requestContext.metadata.d3AValidationContext.validationList),
      );
    }
  }, [props.requestContext]);

  const handleRefresh = async () => {
    setLoading(true);

    // If all the requests for this request context are approved/denied, we can set the form to readonly.
    let allRequestsApprovedOrDenied: boolean = false;
    try {
      if (props.requestContext?.accessRequestIds?.length > 0) {
        allRequestsApprovedOrDenied = true;
        const requestIds: string[] = props.requestContext.accessRequestIds;
        for (const requestId of requestIds) {
          const getRequestRequest: GetRequestRequest = {
            requestId: requestId,
          };
          const getRequestResponse: GetRequestResult = await getRequest(getRequestRequest);
          if (!['APPROVED', 'DENIED'].includes(getRequestResponse.requestItem.status)) {
            allRequestsApprovedOrDenied = false;
            break;
          }
        }
      }
    } catch (e) {
      console.error('Failed to get the request statuses for the request context: ', e);
    }

    if (
      !allRequestsApprovedOrDenied &&
      (!props.requestContext || activeGroupOrWorkspaceId(props) == props.requestContext.ownerId)
    ) {
      setReadOnly(false);
    }

    // first, use the request context directly if it is provided
    let dataAgreementId;
    if (!!props.requestContext) {
      dataAgreementId = props.requestContext.requestSchema.dataAgreementId;
      setFormData(JSON.parse(props.requestContext.dataString));
    }
    // otherwise, data agreement ID must be provided.
    else {
      dataAgreementId = props.dataAgreementId;
    }
    const getDataAgreementOutput = await getDataAgreement({ id: dataAgreementId });
    setDataAgreement(getDataAgreementOutput.dataAgreements[0]);
    setLoading(false);
  };

  const convertValidationsToD3aValidations = (validations: Validation[]) => {
    let convertedValidationList: D3AValidation[] = [];
    validations?.forEach((val) => {
      convertedValidationList.push({
        attribute: val.attribute,
        value: val.value,
        jsonPath: val.jsonPath,
        validationResult: val.validationResult as D3AValidationResult,
        validationTypes: val.d3aValidationTypes,
      });
    });
    return convertedValidationList;
  };

  const convertD3aValidationsToValidations = (validations: D3AValidation[]) => {
    let convertedValidationList: Validation[] = [];
    validations?.forEach((val) => {
      convertedValidationList.push({
        attribute: val.attribute,
        value: val.value,
        jsonPath: val.jsonPath,
        validationResult: val.validationResult as ValidationResult,
        d3aValidationTypes: val.validationTypes,
      });
    });
    return convertedValidationList;
  };

  const save = async () => {
    setSaveLoading(true);

    let convertedValidationList: D3AValidationList = convertValidationsToD3aValidations(validationList);

    // if not already exists: create
    if (!props.requestContext) {
      const createRequestContextOutput = await createRequestContext({
        dataString: JSON.stringify(formData),
        metadata: {
          correspondenceMessages: [],
          d3AValidationContext: {
            isValid: isValid,
            overrideMessage: overrideMessage,
            validationList: convertedValidationList,
          },
        },
        requestSchema: {
          dataAgreementId: dataAgreement.id,
          dataAgreementSchemaString: JSON.stringify(dataAgreement.jsonSchema),
          dataAgreementVersion: dataAgreement.versionId,
          dataAgreementName: dataAgreement.name,
          dataAgreementType: dataAgreement.type,
        },
        type: 'D3ADataUsageAgreement',
        contextScope: 'AccessRequest',
        ownerId: activeGroupOrWorkspaceId(props),
      });
      props.setRequestContext(createRequestContextOutput.userRequestContext);
    } else {
      let updateRequestContextRequest: UpdateRequestContextRequest = {
        id: props.requestContext.requestContextId,
        contextScope: props.requestContext.contextScope,
        metadata: {
          d3AValidationContext: {
            isValid: isValid,
            overrideMessage: overrideMessage,
            validationList: convertedValidationList,
          },
          correspondenceMessages: [...props.requestContext.metadata.correspondenceMessages],
        },
      };
      // only provide dataString if it has been edited - this will cause a new versionId to be created for the request context.
      if (
        props.requestContext?.dataString != undefined &&
        JSON.stringify(formData) !== props.requestContext?.dataString
      ) {
        updateRequestContextRequest.dataString = JSON.stringify(formData);
      }
      const updateRequestContextResponse: UpdateRequestContextResponse = await updateRequestContext(
        updateRequestContextRequest,
      );
      props.setRequestContext(updateRequestContextResponse.userRequestContext);
    }
    setSaveLoading(false);
  };

  const validate = async () => {
    setValidationLoading(true);
    try {
      const validateContextForDataAgreementOutput: ValidateContextForDataAgreementOutput =
        await validateContextForDataAgreement({
          id: dataAgreement.id,
          versionId: dataAgreement.versionId,
          context: formData,
        });
      setIsValid(validateContextForDataAgreementOutput.isValid);
      setValidationList(validateContextForDataAgreementOutput.validationList);
      setValidationErrorMessage(undefined);
    } catch (e) {
      console.log('Error when validating form inputs: ', e);
      setIsValid(false);
      setValidationList([]);
      setValidationErrorMessage(`Internal error when validating: ${e.message}`);
    }
    setValidationLoading(false);
    setIsUnknown(false);
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <SpaceBetween size={'m'} direction={'vertical'}>
      {props.displayMetadataContainer && (
        <RequestContextMetadataContainer
          requestContext={props.requestContext}
          workspaceNameMap={props.workspaceNameMap}
        />
      )}
      <UsageAgreementForm
        schema={JSON.stringify(dataAgreement.jsonSchema)}
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        readOnly={readOnly}
        omniContext={props.omniContext}
      />
      <ValidateFormDataWithD3a
        readonly={readOnly}
        loading={validationLoading}
        formData={formData}
        isValid={isValid}
        isUnknown={isUnknown}
        validationList={validationList}
        validationErrorMessage={validationErrorMessage}
        validate={validate}
        overrideReason={overrideMessage}
        setOverrideReason={setOverrideMessage}
      />
      {props.displaySaveButton && (
        <Header
          actions={
            <Button disabled={readOnly || saveLoading} variant={'normal'} onClick={save}>
              Save responses
            </Button>
          }
        />
      )}
      {props.requestContext && (
        <RequestContextComments
          readOnly={false}
          requestContext={props.requestContext}
          setRequestContext={props.setRequestContext}
        />
      )}
    </SpaceBetween>
  );
});
